import { LICENSES_BASE_ROUTE } from '@koda/koda-utils/constants/licenses';
import {
  BUSINESS_INFORMATION_ROUTE,
  CUSTOMER_INFORMATION_ROUTE,
  INITIAL_INFORMATION_ROUTE,
  LANDING_PAGE_ROUTE,
  PURCHASE_INQUIRY_ROUTE,
  RECEIPT_ROUTE,
  StepRoute,
  SUMMARY_PAYMENT_ROUTE,
} from '../../../../i18n/pathnames/licenses';
import { getCustomerInformationSchema } from './(steps)/customer-information/customerInformation.schema';
import { LicenseStore } from './licenseStore';
import type { LicenseCode, SanityLicense } from './types/licenses';
import { LicensePlanSchema, LicenseTypes } from './types/licenses';
import { Panel } from './types/panelTypes';

const PANELS_SUMMARY_INFO: Panel[] = ['summary', 'info'];
const PANELS_BASKET_INFO: Panel[] = ['basket', 'info'];

export const LicenseFlowType = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
} as const;

export type LicenseFlowTypeValue = (typeof LicenseFlowType)[keyof typeof LicenseFlowType];

export type Step = {
  removeFromProgress?: boolean;
  stepRoute: StepRoute;
  validateWithSelector?: (licenseStore: LicenseStore) => boolean;
  panels?: Panel[];
};

// Validators

const isCustomerInformationValid =
  (license: SanityLicense) =>
  (store: LicenseStore): boolean => {
    const result = getCustomerInformationSchema(license).safeParse(store.customerInformation);
    return result.success;
  };

const isBusinessInformationValid =
  (route: StepRoute) =>
  (store: LicenseStore): boolean =>
    store.stepValidity?.[route] === true;

const isLicensePlanValid = (store: LicenseStore): boolean => LicensePlanSchema.safeParse(store.licensePlan).success;

const isTermsAccepted = (store: LicenseStore): boolean => store.paymentInformation.acceptTerms === true;

// STEPS

const BUSINESS_INFORMATION_WITH_LICENSE_PLAN: Step = {
  stepRoute: BUSINESS_INFORMATION_ROUTE,
  validateWithSelector: (store: LicenseStore) =>
    isBusinessInformationValid(BUSINESS_INFORMATION_ROUTE)(store) && isLicensePlanValid(store),
  panels: PANELS_SUMMARY_INFO,
};

const BUSINESS_INFORMATION: Step = {
  stepRoute: BUSINESS_INFORMATION_ROUTE,
  validateWithSelector: (store: LicenseStore) => isBusinessInformationValid(BUSINESS_INFORMATION_ROUTE)(store),
  panels: PANELS_SUMMARY_INFO,
};

const customerInformationStep = (license: SanityLicense, panels: Panel[]): Step => ({
  stepRoute: CUSTOMER_INFORMATION_ROUTE,
  panels,
  validateWithSelector: (store: LicenseStore) => isCustomerInformationValid(license)(store),
});

const summaryPaymentStep = (options?: { requireTerms?: boolean }): Step => {
  const requireTerms = options?.requireTerms === true;
  return {
    stepRoute: SUMMARY_PAYMENT_ROUTE,
    validateWithSelector: requireTerms ? isTermsAccepted : undefined,
  };
};

const purchaseInquiryStep = (license: SanityLicense): Step => ({
  stepRoute: PURCHASE_INQUIRY_ROUTE,
  validateWithSelector: isCustomerInformationValid(license),
  removeFromProgress: true,
});

// FLOWS

const buildFlowA = (license: SanityLicense): Step[] => [
  BUSINESS_INFORMATION_WITH_LICENSE_PLAN,
  customerInformationStep(license, PANELS_BASKET_INFO),
  {
    ...summaryPaymentStep({ requireTerms: true }),
    panels: PANELS_BASKET_INFO,
  },
];

const buildFlowB = (license: SanityLicense): Step[] => [
  BUSINESS_INFORMATION,
  customerInformationStep(license, PANELS_SUMMARY_INFO),
  {
    ...summaryPaymentStep(),
    panels: PANELS_SUMMARY_INFO,
  },
  purchaseInquiryStep(license),
];

const buildFlowC = (license: SanityLicense): Step[] => [
  { stepRoute: LANDING_PAGE_ROUTE, panels: PANELS_SUMMARY_INFO },
  customerInformationStep(license, PANELS_SUMMARY_INFO),
  purchaseInquiryStep(license),
];

const buildFlowCGeneric = (license: SanityLicense): Step[] => [
  { ...customerInformationStep(license, PANELS_SUMMARY_INFO), removeFromProgress: true },
  purchaseInquiryStep(license),
];

const buildFlowD = (license: SanityLicense): Step[] => [
  BUSINESS_INFORMATION_WITH_LICENSE_PLAN,
  customerInformationStep(license, PANELS_BASKET_INFO),
  {
    ...summaryPaymentStep({ requireTerms: true }),
    panels: PANELS_BASKET_INFO,
  },
  purchaseInquiryStep(license),
];

const INITIAL_INFORMATION_STEP: Step = {
  stepRoute: INITIAL_INFORMATION_ROUTE,
  validateWithSelector: isBusinessInformationValid(INITIAL_INFORMATION_ROUTE),
  panels: PANELS_SUMMARY_INFO,
};

const getDefaultFlow = (license: SanityLicense): Step[] => {
  const { flowType } = license;
  const licenseCode = license?.licenseId as LicenseCode | null;
  if (!licenseCode) return []; // Fallback if licenseCode is null

  switch (flowType) {
    case LicenseFlowType.A:
      return buildFlowA(license);
    case LicenseFlowType.B:
      return buildFlowB(license);
    case LicenseFlowType.D:
      return buildFlowD(license);
    case LicenseFlowType.C:
      if (licenseCode == LicenseTypes.OtherContactForm) return buildFlowCGeneric(license);
      return buildFlowC(license);
    default:
      return [];
  }
};

export const hasInitialInformation = (license: SanityLicense): boolean =>
  // Currently only Theater has an initial-information step.
  // Keeping this lightweight avoids importing all schemas into the client bundle via FlowController.
  license.licenseId === LicenseTypes.Theater;

// Adds a "previous page" and "receipt" step at the start and end of the flow
export const getFlow = (license: SanityLicense): Step[] => {
  if (!license.licenseId) return [];

  const initialInformationSteps =
    hasInitialInformation(license) && license.flowType !== LicenseFlowType.C ? [INITIAL_INFORMATION_STEP] : [];

  return [
    {
      stepRoute: LICENSES_BASE_ROUTE,
      removeFromProgress: true,
    },
    ...initialInformationSteps,
    ...getDefaultFlow(license),
    {
      stepRoute: RECEIPT_ROUTE,
      removeFromProgress: true,
    },
  ];
};
